import { useGetCurrenciesQuery } from "@/api/AppApi/services/company";
import { storeCurrencyCookie } from "@/utils/functions/cookieOperations";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { currencyAtom, currencyCodeAtom } from "./currencyAtom";

/**
 * @param serverCurrencyCode the currency the SERVER resolved for this visitor
 *   (from their country, narrowed to what the store sells) and already priced the
 *   SSR markup in. Seeding from it is what stops the first visit disagreeing with
 *   the refresh: `currencies.find(c => c.selected)` below is a STORE-level setting,
 *   identical for every visitor, so on a first visit (empty localStorage) it used
 *   to overwrite the server's per-location currency and the prices changed on the
 *   next load.
 */
export const useInitGlobalState = (serverCurrencyCode?: string) => {
  const getCurrincies = useGetCurrenciesQuery();
  const currencies = getCurrincies?.data?.result;
  const [currCurrencyCode, setCurrCurrencyCode] = useAtom(currencyCodeAtom);
  const [_, setCurrentCurrency] = useAtom(currencyAtom);
  useEffect(() => {
    if (getCurrincies.isLoading) return;
    let selectedCurrencyCode = currCurrencyCode;
    if (
      !currCurrencyCode ||
      currCurrencyCode === "" ||
      !currencies?.map((curr) => curr.code).includes(selectedCurrencyCode)
    ) {
      const supported = currencies?.map((curr) => curr.code) ?? [];
      const fromServer = serverCurrencyCode?.trim().toUpperCase() ?? "";
      const defaultCurrency = currencies ? currencies[0]?.code : "";
      // Server first — it already priced this page. Only fall back to the store's
      // `selected` when the server could not resolve one (no currency list yet).
      selectedCurrencyCode =
        (fromServer && supported.includes(fromServer) ? fromServer : "") ||
        currencies?.find((curr) => curr.selected)?.code ||
        defaultCurrency;
    }
    setCurrCurrencyCode(selectedCurrencyCode);
    currencies &&
      setCurrentCurrency(
        currencies?.find((curr) => curr.code === selectedCurrencyCode) ??
          currencies[0],
      );
    storeCurrencyCookie(selectedCurrencyCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCurrincies.isLoading, serverCurrencyCode]);
};
// , router.locale
